import { TerminalProvider } from "@lemmonhq/types";
import payrexxLogo from "@app/images/partner-logos/payrexx.png";
import nakkaLogo from "@app/images/partner-logos/nakka.png";
import stripeLogo from "@app/images/partner-logos/stripe.svg?url";
import adyenLogo from "@app/images/partner-logos/adyen.svg?url";

/**
 * Per-provider terminal registration config.
 *
 * This is the single place to customize how each provider registers a terminal:
 * which fields to collect, how many wizard steps, and which backend mutation to
 * call. Adding a provider or changing its flow means editing only this file —
 * the form component renders generically from it.
 */

// Fields a provider can collect. `name` is always shown first and handled by the
// form itself, so it is not listed here.
export type TerminalFieldKey =
  | "serial_number"
  | "terminal_id"
  | "password"
  | "client_id"
  | "client_secret"
  | "otp";

export type TerminalField = {
  key: TerminalFieldKey;
  /** i18n key for the label. */
  labelKey: string;
  /** i18n key for the placeholder. */
  placeholderKey?: string;
  type?: "text" | "password" | "number";
  mono?: boolean;
  required?: boolean;
  /** Render as a large, centered one-time-code input (and never persist it). */
  otp?: boolean;
};

export type TerminalStep = {
  /** i18n key for the step label (shown in the wizard stepper). */
  titleKey: string;
  /** Optional i18n keys for numbered instructions at the top of the step. */
  instructionKeys?: string[];
  fields: TerminalField[];
};

export type TerminalProviderConfig = {
  value: TerminalProvider;
  label: string;
  icon: string;
  logo: string | null;
  /** Which mutation registers this provider's terminal. */
  action: "create" | "pair";
  /** One step = a plain form; multiple steps = a wizard (create mode). */
  steps: TerminalStep[];
};

const NS = "settings.terminals";

export const TERMINAL_PROVIDERS: TerminalProviderConfig[] = [
  {
    value: "nakka",
    label: "Nakka",
    icon: "payment",
    logo: nakkaLogo,
    action: "create",
    steps: [
      {
        titleKey: `${NS}.pair_step_details`,
        fields: [
          {
            key: "terminal_id",
            labelKey: `${NS}.terminal_id_label`,
            placeholderKey: `${NS}.terminal_id_placeholder`,
            type: "number",
            mono: true,
          },
          {
            key: "client_id",
            labelKey: `${NS}.gateway_client_id`,
            placeholderKey: `${NS}.gateway_client_id_placeholder`,
            mono: true,
          },
          {
            key: "client_secret",
            labelKey: `${NS}.gateway_client_secret`,
            placeholderKey: `${NS}.gateway_client_secret_placeholder`,
            type: "password",
            mono: true,
          },
        ],
      },
    ],
  },
  {
    value: "payrexx",
    label: "Payrexx",
    icon: "credit_card",
    logo: payrexxLogo,
    action: "pair",
    steps: [
      // Step 1: name + staff (name/staff are rendered by the form itself).
      { titleKey: `${NS}.pair_step_details`, fields: [] },
      // Step 2: pair with serial number + one-time code.
      {
        titleKey: `${NS}.pair_step_pairing`,
        instructionKeys: [
          `${NS}.pair_instruction_open`,
          `${NS}.pair_instruction_enter`,
          `${NS}.pair_instruction_stay`,
        ],
        fields: [
          {
            key: "serial_number",
            labelKey: `${NS}.serial_number`,
            placeholderKey: `${NS}.serial_number_placeholder`,
            mono: true,
            required: true,
          },
          {
            key: "otp",
            labelKey: `${NS}.otp`,
            placeholderKey: `${NS}.otp_placeholder`,
            mono: true,
            required: true,
            otp: true,
          },
        ],
      },
    ],
  },
  {
    value: "adyen",
    label: "Adyen",
    icon: "point_of_sale",
    logo: adyenLogo,
    action: "create",
    steps: [
      {
        titleKey: `${NS}.pair_step_details`,
        fields: [
          {
            key: "serial_number",
            labelKey: `${NS}.serial_number`,
            placeholderKey: `${NS}.serial_number_placeholder`,
            mono: true,
          },
        ],
      },
    ],
  },
  {
    value: "stripe",
    label: "Stripe",
    icon: "credit_score",
    logo: stripeLogo,
    action: "create",
    steps: [
      {
        titleKey: `${NS}.pair_step_details`,
        fields: [
          {
            key: "serial_number",
            labelKey: `${NS}.serial_number`,
            placeholderKey: `${NS}.serial_number_placeholder`,
            mono: true,
          },
        ],
      },
    ],
  },
];

export const getTerminalProviderConfig = (
  provider: TerminalProvider | string | null | undefined,
): TerminalProviderConfig =>
  TERMINAL_PROVIDERS.find((p) => p.value === provider) ?? TERMINAL_PROVIDERS[0];
